import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Space,
  Tag,
  Popconfirm,
  Tooltip,
  Select,
  Statistic,
  Row,
  Col,
  Spin,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { companyService } from "../../../services";

const { Title, Text } = Typography;
const { Option } = Select;

const Companies = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [pageCache, setPageCache] = useState({});
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Load companies from API with caching
  const loadCompanies = async (
    page = 1,
    pageSize = 10,
    search = "",
    status = "all",
    forceRefresh = false,
  ) => {
    try {
      setLoading(true);

      // Create a cache key based on all filters
      const cacheKey = `${page}-${pageSize}-${search}-${status}`;

      // Check if we have cached data for this page and not forcing refresh
      if (!forceRefresh && pageCache[cacheKey]) {
        setCompanies(pageCache[cacheKey].data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: pageCache[cacheKey].total,
        });
        setLoading(false);
        return;
      }

      const response = await companyService.getCompanies(
        page,
        pageSize,
        search,
        status,
      );

      if (response.status) {
        // Store in cache
        setPageCache((prev) => ({
          ...prev,
          [cacheKey]: {
            data: response.data.companies || [],
            total: response.data.pagination?.totalItems || 0,
          },
        }));

        setCompanies(response.data.companies || []);
        setPagination({
          current: response.data.pagination?.currentPage || page,
          pageSize: pageSize,
          total: response.data.pagination?.totalItems || 0,
        });

        // Load stats
        await loadCompanyStats();
      } else {
        message.error(response.message || "Failed to load companies");
        setCompanies([]);
        setPagination({
          current: 1,
          pageSize: pageSize,
          total: 0,
        });
      }
    } catch (error) {
      console.error("Load companies error:", error);
      message.error("Failed to load companies");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Load company statistics
  const loadCompanyStats = async () => {
    try {
      const response = await companyService.getCompanyStats();
      if (response.status) {
        setStats({
          totalCompanies: response.data.totalCompanies || 0,
          activeCompanies: response.data.activeCompanies || 0,
          totalUsers: response.data.totalUsers || 0,
        });
      }
    } catch (error) {
      console.error("Load company stats error:", error);
    }
  };

  // Initial load on component mount
  useEffect(() => {
    loadCompanies(1, pagination.pageSize, searchText, statusFilter);
  }, []);

  // Handle filter changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      // Clear all cache when filters change
      setPageCache({});
      loadCompanies(1, pagination.pageSize, searchText, statusFilter);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, statusFilter]);

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space>
          <BankOutlined style={{ color: "#1890ff" }} />
          <div>
            <div className="font-medium">{name}</div>
            {record.description && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {record.description}
              </Text>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Users",
      dataIndex: "userCount",
      key: "userCount",
      render: (count) => (
        <Space>
          <UserOutlined />
          <Text>{count || 0}</Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Company">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete Company"
            description="Are you sure you want to delete this company? All users will be moved to default company."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Tooltip title="Delete Company">
              <Button type="link" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAddCompany = () => {
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const response = await companyService.createCompany({
        name: values.name,
        description: values.description,
      });

      if (response.status) {
        message.success("Company created successfully!");
        setIsModalVisible(false);
        form.resetFields();

        // Clear cache and reload from page 1
        setPageCache({});
        // Reset to page 1 and reload
        await loadCompanies(1, pagination.pageSize, searchText, statusFilter);
        await loadCompanyStats();
      } else {
        message.error(response.message || "Failed to create company");
      }
    } catch (error) {
      console.error("Create company error:", error);
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      message.error("Failed to create company");
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    editForm.setFieldsValue({
      name: company.name,
      description: company.description,
      status: company.status,
    });
    setIsEditModalVisible(true);
  };

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields();

      const response = await companyService.updateCompany(
        editingCompany.id,
        values,
      );

      if (response.status) {
        message.success("Company updated successfully!");
        setIsEditModalVisible(false);
        editForm.resetFields();
        setEditingCompany(null);

        // Clear cache and reload from page 1
        setPageCache({});
        // Reset to page 1 and reload
        await loadCompanies(1, pagination.pageSize, searchText, statusFilter);
        await loadCompanyStats();
      } else {
        message.error(response.message || "Failed to update company");
      }
    } catch (error) {
      console.error("Update company error:", error);
      message.error("Failed to update company");
    }
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    editForm.resetFields();
    setEditingCompany(null);
  };

  const handleDelete = async (companyId) => {
    try {
      const response = await companyService.deleteCompany(companyId);

      if (response.status) {
        message.success("Company deleted successfully!");

        setPageCache({});

        let targetPage = pagination.current;

        if (companies.length === 1 && pagination.current > 1) {
          targetPage = pagination.current - 1;
        }

        // Load the appropriate page
        await loadCompanies(
          targetPage,
          pagination.pageSize,
          searchText,
          statusFilter,
        );
        await loadCompanyStats();
      } else {
        message.error(response.message || "Failed to delete company");
      }
    } catch (error) {
      console.error("Delete company error:", error);
      message.error("Failed to delete company");
    }
  };

  const handleTableChange = (newPagination) => {
    loadCompanies(
      newPagination.current,
      newPagination.pageSize,
      searchText,
      statusFilter,
    );
  };

  const handleRefresh = () => {
    // Clear cache for current page and force refresh
    const currentCacheKey = `${pagination.current}-${pagination.pageSize}-${searchText}-${statusFilter}`;
    setPageCache((prev) => {
      const newCache = { ...prev };
      delete newCache[currentCacheKey];
      return newCache;
    });
    loadCompanies(
      pagination.current,
      pagination.pageSize,
      searchText,
      statusFilter,
      true,
    );
    loadCompanyStats();
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2}>Companies Management</Title>
          <Text type="secondary">
            Super Admin: Manage all companies in the system
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            size="large"
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCompany}
            size="large"
          >
            Add Company
          </Button>
        </Space>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={8}>
          <Card className="text-center">
            <Statistic
              title="Total Companies"
              value={stats.totalCompanies}
              prefix={<BankOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="text-center">
            <Statistic
              title="Active Companies"
              value={stats.activeCompanies}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="text-center">
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <div className="mb-4">
          <Alert
            message="Super Admin Access"
            description="You have full access to manage all companies. Regular admins can only manage users within their assigned company."
            type="info"
            showIcon
          />
        </div>

        {/* Search and Filter */}
        <div className="flex justify-between mb-4">
          <Input
            placeholder="Search companies..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            size="large"
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={handleStatusFilter}
            style={{ width: 150 }}
            size="large"
            allowClear
          >
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </div>

        {loading && companies.length === 0 ? (
          <div className="text-center py-12">
            <Spin size="large" />
            <div className="mt-4">
              <Text>Loading companies...</Text>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={companies}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} companies`,
            }}
            onChange={handleTableChange}
            rowKey="id"
            locale={{
              emptyText: "No companies found",
            }}
          />
        )}
      </Card>

      {/* Add Company Modal */}
      <Modal
        title="Add New Company"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Create Company"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="addCompanyForm">
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: "Please input company name!" }]}
          >
            <Input
              size="large"
              placeholder="Enter company name"
              prefix={<BankOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please input description!" }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Describe what this company does..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
        title={`Edit Company - ${editingCompany?.name}`}
        open={isEditModalVisible}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Update Company"
        cancelText="Cancel"
        width={600}
      >
        <Form form={editForm} layout="vertical" name="editCompanyForm">
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: "Please input company name!" }]}
          >
            <Input
              size="large"
              placeholder="Enter company name"
              prefix={<BankOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please input description!" }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Describe what this company does..."
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status!" }]}
          >
            <Select size="large" placeholder="Select status">
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Companies;
